/**
 * Money > Insights. Four things, one screen: the household health score, the
 * subscription audit, savings goals, and the house rules.
 *
 * Adults only — enforced by the Worker (worker/index.ts gates /api/insights) and
 * by the route wrapper in App.tsx. Nothing here trusts the front end.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import HealthPanel from './HealthPanel';
import SubscriptionsPanel from './SubscriptionsPanel';
import GoalsPanel from './GoalsPanel';
import RulebookPanel from './RulebookPanel';
import { cx } from './ui';

const TABS = [
  { id: 'health', label: 'Health score', render: () => <HealthPanel /> },
  { id: 'subscriptions', label: 'Subscriptions', render: () => <SubscriptionsPanel /> },
  { id: 'goals', label: 'Savings goals', render: () => <GoalsPanel /> },
  { id: 'rulebook', label: 'House rules', render: () => <RulebookPanel /> },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function InsightsPage() {
  const [active, setActive] = useState<TabId>('health');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    else return;

    e.preventDefault();
    setActive(TABS[next].id);
    tabRefs.current[next]?.focus();
  }

  const current = TABS.find((t) => t.id === active) ?? TABS[0];

  return (
    <div>
      <div className="mb-6">
        <p className="text-sm text-ink-soft">
          <Link to="/money" className="underline underline-offset-2 hover:text-ink">
            Money
          </Link>{' '}
          / Insights
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold text-ink">Insights</h1>
        <p className="mt-1 max-w-prose text-ink-soft">
          How the month is actually going, what keeps charging us, what we are saving for, and the rules
          we set for ourselves.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Insights sections"
        className="mb-6 flex gap-2 overflow-x-auto border-b border-border pb-px"
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={active === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={active === tab.id ? 0 : -1}
            onClick={() => setActive(tab.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cx(
              'min-h-11 shrink-0 rounded-t-lg border-b-2 px-4 text-sm font-medium whitespace-nowrap transition-colors',
              'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none',
              active === tab.id
                ? 'border-sage text-sage'
                : 'border-transparent text-ink-soft hover:bg-surface-sunk hover:text-ink'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`panel-${current.id}`}
        aria-labelledby={`tab-${current.id}`}
        tabIndex={0}
        className="focus-visible:outline-none"
      >
        {current.render()}
      </div>
    </div>
  );
}
